import React, { useState, useEffect, useCallback } from 'react';
import type { ProductImage } from '../../type';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Download, FileText, FileSpreadsheet, Loader2, PlayCircle, X, Minus, Plus, Box, ImageOff } from 'lucide-react';
import { normalizeUrl } from '@univdiam/ui/lib/UrlNormalize';
import { cn } from '@univdiam/ui/lib/utils';
import { Button } from '../../components/button';

// Extended ProductImage type to support multiple media types
interface ProductMedia extends ProductImage {
  type?: 'image' | 'video' | 'pdf' | 'excel' | 'iframe';
  filename?: string;
}

interface ProductGalleryProps {
  images?: ProductImage[];
  aspectRatio?: number;
  iFrameLink?: string;
}

// Detect media type from URL
const detectMediaType = (src: string): ProductMedia['type'] => {
  const lower = src.toLowerCase();
  if (lower.match(/\.(mp4|webm|ogg|mov)$/)) return 'video';
  if (lower.match(/\.(pdf)$/)) return 'pdf';
  if (lower.match(/\.(xlsx?|csv)$/)) return 'excel';
  return 'image';
};

// Get filename from URL
const getFilename = (src: string): string => {
  try {
    const url = new URL(src);
    const pathname = url.pathname;
    return pathname.split('/').pop() || 'download';
  } catch {
    return src.split('/').pop() || 'download';
  }
};

// Download handler
const handleDownload = async (url: string) => {
  try {
    const response = await fetch(url, {
      headers: {
        'Accept': 'video/mp4,video/*,*/*',
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const blob = await response.blob()
    const contentType = response.headers.get('content-type')

    // Determine file extension from content type or URL
    let extension = 'mp4'
    if (contentType?.includes('webm')) extension = 'webm'
    if (contentType?.includes('quicktime')) extension = 'mov'
    if (contentType?.includes('x-m4v')) extension = 'm4v'

    const fileName = 'UnivDiam_' + (url.split('/').pop()?.split('?')[0] || `video.${extension}`)

    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)

  } catch (err) {
    console.error('Download failed:', err)
  }
};

const VideoThumbnail: React.FC<{ src: string }> = ({ src }) => {
  return (
    <div className="relative w-full h-full bg-foreground">
      <video
        src={normalizeUrl(src)}
        className="w-full h-full object-cover"
        muted
        playsInline
        preload="metadata"
      />
      <div className="absolute inset-0 bg-foreground/30 flex items-center justify-center">
        <PlayCircle className="w-6 h-6 text-background drop-shadow" />
      </div>
    </div>
  );
};

// Keeps the previous image on screen while the next src downloads,
// so switching variants never flashes an empty main section
const SmoothImage: React.FC<{
  src: string;
  alt?: string;
  onClick?: () => void;
}> = ({ src, alt, onClick }) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [isSwapping, setIsSwapping] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (src === currentSrc) return;
    let cancelled = false;
    setIsSwapping(true);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setCurrentSrc(src);
      setIsSwapping(false);
    };
    img.onerror = () => {
      if (cancelled) return;
      // Show the new (broken) src anyway rather than silently keeping stale one
      setCurrentSrc(src);
      setIsSwapping(false);
    };
    img.src = src;
    return () => { cancelled = true; };
  }, [src, currentSrc]);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {!hasLoadedOnce && (
        <div className="absolute inset-0 rounded-lg bg-muted animate-pulse" />
      )}
      <img
        src={currentSrc}
        alt={alt}
        decoding="async"
        onLoad={() => setHasLoadedOnce(true)}
        className="relative max-w-full max-h-full object-contain rounded-lg cursor-pointer transition-transform duration-300 hover:scale-105"
        onClick={onClick}
      />
      {isSwapping && (
        <div className="absolute bottom-3 right-3 rounded-full bg-black/50 p-2">
          <Loader2 className="w-4 h-4 animate-spin text-white" />
        </div>
      )}
    </div>
  );
};

const MediaThumbnail: React.FC<{
  item: ProductMedia;
  isSelected: boolean;
  onClick: () => void;
}> = ({ item, isSelected, onClick }) => {
  const renderThumbnailContent = () => {
    switch (item.type) {
      case 'video':
        return <VideoThumbnail src={item.src} />;
      case 'pdf':
        return (
          <div className="w-full h-full bg-destructive/10 flex flex-col items-center justify-center gap-1">
            <FileText className="w-6 h-6 text-destructive" />
            <span className="text-caption2 text-destructive font-medium">PDF</span>
          </div>
        );
      case 'excel':
        return (
          <div className="w-full h-full bg-chart-2/10 flex flex-col items-center justify-center gap-1">
            <FileSpreadsheet className="w-6 h-6 text-chart-2" />
            <span className="text-caption2 text-chart-2 font-medium">Excel</span>
          </div>
        );
      case 'iframe':
        return (
          <div className="w-full h-full bg-chart-3/10 flex flex-col items-center justify-center gap-1">
            <Box className="w-6 h-6 text-chart-3" />
            <span className="text-caption2 text-chart-3 font-medium">3D</span>
          </div>
        );
      default:
        return (
          <img
            src={normalizeUrl(item.thumbnail || item.src)}
            alt={`Thumbnail of ${item.alt}`}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              // Thumb missing (older seed) — fall back to full-res image
              if (item.thumbnail && e.currentTarget.src !== normalizeUrl(item.src)) {
                e.currentTarget.src = normalizeUrl(item.src)
              }
            }}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        );
    }
  };

  return (
    <button
      type="button"
      className={cn(
        'group relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-muted ring-1 ring-border transition-all duration-200',
        isSelected
          ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-md'
          : 'hover:ring-primary/40 hover:shadow-sm opacity-80 hover:opacity-100'
      )}
      onClick={onClick}
      aria-label={item.alt || 'View media'}
      aria-current={isSelected}
    >
      {renderThumbnailContent()}
    </button>
  );
};

const MediaDisplay: React.FC<{
  item: ProductMedia;
  onDownload?: () => void;
  onClick?: () => void;
}> = ({ item, onDownload, onClick }) => {
  const renderContent = () => {
    switch (item.type) {
      case 'video':
        return (
          <video
            src={normalizeUrl(item.src)}
            controls
            className="max-w-full max-h-full rounded-lg cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            Your browser does not support the video tag.
          </video>
        );
      case 'pdf':
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-card rounded-lg shadow-lg p-8 max-w-sm text-center">
              <FileText className="w-24 h-24 text-destructive mx-auto mb-4" />
              <h3 className="text-heading6 font-semibold mb-2">PDF Document</h3>
              <Button onClick={onDownload} variant="destructive">
                <Download className="w-5 h-5" />
                Download PDF
              </Button>
            </div>
          </div>
        );
      case 'excel':
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-card rounded-lg shadow-lg p-8 max-w-sm text-center">
              <FileSpreadsheet className="w-24 h-24 text-chart-2 mx-auto mb-4" />
              <h3 className="text-heading6 font-semibold mb-2">Excel Document</h3>
              <Button onClick={onDownload} variant="ghost" className="bg-chart-2 text-white hover:bg-chart-2/90">
                <Download className="w-5 h-5" />
                Download Excel
              </Button>
            </div>
          </div>
        );
      default:
        return (
          <SmoothImage
            src={normalizeUrl(item.src)}
            alt={item.alt}
            onClick={onClick}
          />
        );
    }
  };

  return <div className="absolute inset-0 flex items-center justify-center">{renderContent()}</div>;
};

const Lightbox: React.FC<{
  images: ProductMedia[];
  startIndex: number | null;
  onClose: () => void;
  iFrameLink?: string;
}> = ({ images, startIndex, onClose, iFrameLink }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex ?? 0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const totalItems = images.length + (iFrameLink ? 1 : 0);

  useEffect(() => {
    if (startIndex !== null) {
      setCurrentIndex(startIndex);
      setZoomLevel(1);
    }
  }, [startIndex]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalItems);
    setZoomLevel(1);
  }, [totalItems]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalItems) % totalItems);
    setZoomLevel(1);
  }, [totalItems]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleDownloadCurrent = () => {
    const currentItem = images[currentIndex];
    if (currentItem) {
      handleDownload(currentItem.src);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };
    if (startIndex !== null) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, onClose, startIndex]);

  const isOpen = startIndex !== null;
  if (!isOpen) return null;

  const isIframe = iFrameLink && currentIndex === totalItems - 1;
  const image = images[currentIndex];

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[9999] bg-black/25 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-[9999] w-[99vw] h-[98vh] max-w-none translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 focus:outline-none p-0 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
            {/* Close Button */}
            <DialogPrimitive.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white z-[100] focus-visible:ring-white"
              >
                <X className="h-6 w-6" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>

            {/* Image Counter */}
            {totalItems > 1 && (
              <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium z-[100]">
                {currentIndex + 1} / {totalItems}
              </div>
            )}

            {/* Zoom Controls (Images Only) */}
            {!isIframe && image && image.type === 'image' && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black/50 rounded-full p-2 z-[100]">
                <Button onClick={handleZoomOut} variant="ghost" size="icon" className="text-white hover:bg-white/20 hover:text-white rounded-full" aria-label="Zoom Out" disabled={zoomLevel <= 0.5}>
                  <Minus className="w-5 h-5" />
                </Button>
                <Button onClick={handleResetZoom} variant="ghost" size="sm" className="text-white hover:bg-white/20 hover:text-white rounded-full font-medium" aria-label="Reset Zoom">
                  {Math.round(zoomLevel * 100)}%
                </Button>
                <Button onClick={handleZoomIn} variant="ghost" size="icon" className="text-white hover:bg-white/20 hover:text-white rounded-full" aria-label="Zoom In" disabled={zoomLevel >= 3}>
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            )}

            {!isIframe && image && (
              <>
                {image.type === 'video' ? (
                  <video
                    src={normalizeUrl(image.src)}
                    controls
                    autoPlay
                    className="max-w-full max-h-full rounded-lg shadow-2xl"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : image.type === 'pdf' || image.type === 'excel' ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="bg-card rounded-lg shadow-2xl p-12 max-w-md text-center">
                      {image.type === 'pdf' ? (
                        <FileText className="w-32 h-32 text-destructive mx-auto mb-6" />
                      ) : (
                        <FileSpreadsheet className="w-32 h-32 text-chart-2 mx-auto mb-6" />
                      )}
                      <h3 className="text-heading4 font-bold mb-3">
                        {image.type === 'pdf' ? 'PDF Document' : 'Excel Document'}
                      </h3>
                      <p className="text-muted-foreground mb-6 text-body1">
                        {image.filename || getFilename(image.src)}
                      </p>
                      <Button
                        onClick={handleDownloadCurrent}
                        size="lg"
                        variant={image.type === 'pdf' ? 'destructive' : 'ghost'}
                        className={image.type === 'excel' ? 'bg-chart-2 text-white hover:bg-chart-2/90' : ''}
                      >
                        <Download className="w-6 h-6" />
                        Download {image.type === 'pdf' ? 'PDF' : 'Excel'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center overflow-auto"
                    style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
                  >
                    <img
                      src={normalizeUrl(image.src)}
                      alt={image.alt}
                      style={{
                        transform: `scale(${zoomLevel})`,
                        transition: 'transform 0.2s ease-out'
                      }}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-2xl pointer-events-auto"
                    />
                  </div>
                )}

                {/* Download button for images and videos */}
                {(image.type === 'image' || image.type === 'video') && (
                  <Button
                    onClick={handleDownloadCurrent}
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-16 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white focus-visible:ring-white z-10"
                    aria-label="Download"
                  >
                    <Download className="w-6 h-6" />
                  </Button>
                )}
              </>
            )}

            {isIframe && (
              <div className="w-[98%] h-[98%] rounded-lg shadow-2xl overflow-hidden border">
                <iframe
                  title="3D Model Lightbox"
                  src={iFrameLink}
                  className="w-full h-full rounded-lg"
                  allow="autoplay; fullscreen; xr-spatial-tracking; web-share"
                />
              </div>
            )}

            {totalItems > 1 && (
              <>
                <Button
                  onClick={handlePrev}
                  variant="ghost"
                  size="icon"
                  aria-label="Previous item"
                  className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white focus-visible:ring-white z-10"
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  onClick={handleNext}
                  variant="ghost"
                  size="icon"
                  aria-label="Next item"
                  className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white focus-visible:ring-white z-10"
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

const ProductGallery = ({
  images = [],
  aspectRatio = 1 / 1,
  iFrameLink
}: ProductGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Clamp selection if the media list shrinks (e.g. variant with fewer items)
  const itemCount = images.length + (iFrameLink ? 1 : 0);
  useEffect(() => {
    if (selectedIndex >= itemCount && itemCount > 0) {
      setSelectedIndex(0);
    }
  }, [itemCount, selectedIndex]);

  // Normalize images from API with type detection
  const normalizedImages: ProductMedia[] = images.map((image) => ({
    ...image,
    type: detectMediaType(image.src),
    filename: getFilename(image.src)
  }));

  // Treat iframe as extra slide
  const totalItems = normalizedImages.length + (iFrameLink ? 1 : 0);

  // A product with only a 3D model still has something to show
  if (totalItems === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 w-full bg-muted rounded-lg"
        style={{ aspectRatio }}
      >
        <ImageOff className="w-10 h-10 text-muted-foreground/60" strokeWidth={1.5} />
        <span className="text-body2 text-muted-foreground">No images available</span>
      </div>
    );
  }

  const openLightbox = (index: number) => {
    // Open lightbox for both images and iframe
    setLightboxIndex(index);
  };

  const closeLightbox = () => setLightboxIndex(null);

  const handleDownloadCurrent = () => {
    const currentItem = normalizedImages[selectedIndex];
    if (currentItem) {
      handleDownload(currentItem.src);
    }
  };

  const currentItem = normalizedImages[selectedIndex];
  const showDownloadButton = currentItem && (currentItem.type === 'image' || currentItem.type === 'video' || currentItem.type === 'pdf' || currentItem.type === 'excel');

  return (
    <>
      <div className="flex flex-col gap-4 h-full min-h-0">
        {/* Main Display Area */}
        <div
          className="flex-1 min-h-0 relative overflow-hidden rounded-xl"
          style={{ aspectRatio }}
        >
          {/* Show current image */}
          {selectedIndex < normalizedImages.length && (
            <MediaDisplay
              item={normalizedImages[selectedIndex]!}
              onDownload={handleDownloadCurrent}
              onClick={() => openLightbox(selectedIndex)}
            />
          )}

          {/* Show iframe */}
          {iFrameLink && selectedIndex === totalItems - 1 && (
            <div className="absolute inset-0 p-2">
              <iframe
                title="3D Model"
                src={iFrameLink}
                className="w-full h-full rounded-lg"
                allow="autoplay; fullscreen; xr-spatial-tracking; web-share"
              />
            </div>
          )}

          {/* Download button overlay for main view */}
          {showDownloadButton && selectedIndex < normalizedImages.length && (
            <Button
              onClick={handleDownloadCurrent}
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white focus-visible:ring-white z-10"
              aria-label="Download"
            >
              <Download className="w-5 h-5" />
            </Button>
          )}
        </div>
        {/* Thumbnails */}
        <div className="flex justify-center gap-3 overflow-x-auto p-1">
          {normalizedImages.map((image, index) => (
            <MediaThumbnail
              key={index}
              item={image}
              isSelected={selectedIndex === index}
              onClick={() => setSelectedIndex(index)}
            />
          ))}

          {iFrameLink && (
            <div
              className={cn(
                'h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-muted ring-1 ring-border cursor-pointer transition-all duration-200',
                selectedIndex === totalItems - 1
                  ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-md'
                  : 'hover:ring-primary/40 hover:shadow-sm opacity-80 hover:opacity-100'
              )}
              onClick={() => setSelectedIndex(totalItems - 1)}
            >
              <iframe
                title="3D Model Thumbnail"
                src={iFrameLink}
                className="w-full h-full rounded-lg pointer-events-none"
              />
            </div>
          )}
        </div>


      </div>

      {/* Lightbox for images and iframe */}
      <Lightbox
        images={normalizedImages}
        startIndex={lightboxIndex}
        onClose={closeLightbox}
        iFrameLink={iFrameLink}
      />
    </>
  );
};

export default ProductGallery;